import { Drawer } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { endOfDay, format, parse, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGetIntegratorRevenueSummaryQuery, useGetIntegratorTotalEarningsQuery } from '@/api/v1';
import { NoResultsView } from '@/components/atoms/NoResultsView';
import { AmountCard } from '@/components/molecules/AmountCard';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { MainScreen } from '@/components/organisms/MainScreen';
import { TransactionsTable } from '@/components/organisms/TransactionsTable';
import { FF_DASHBOARD_INTEGRATOR_EARNINGS_WITHDRAWAL } from '@/data/featureFlags';
import { TimezoneContext } from '@/hooks/useDateFormatter';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { NoActivityView } from '@/Screens/Dashboard/Activity/NoActivityView';
import { DatePreset, TransactionsFilters } from '@/Screens/Dashboard/Activity/TransactionsFilters';
import { CurrencyCodeType } from '@/types/currency';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { SimplifiedTransactionStatus, TransactionType } from '@/types/transaction';
import { EARNINGS_ACTIVITY_COLUMNS } from '@/types/transactionColumn';
import { TransactionFilterField } from '@/types/transactionFilterField';
import { countTransactionFilters } from '@/utility/countTransactionFilters';

const DATE_PARSED_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";
const FILTER_DATE_FORMAT = 'yyyy-MM-dd';

const EARNINGS_VISIBLE_FILTERS: readonly TransactionFilterField[] = [
  TransactionFilterField.FROM_DATE,
  TransactionFilterField.TO_DATE,
  TransactionFilterField.AMOUNT,
  TransactionFilterField.CURRENCY,
  TransactionFilterField.SENDER,
  TransactionFilterField.RECIPIENT,
  TransactionFilterField.TRANSACTION_ID,
];

const computeDefaultDates = (timezone: string): { fromDate: string; toDate: string } => {
  const now = new Date();
  return {
    fromDate: formatInTimeZone(startOfMonth(now), timezone, DATE_PARSED_FORMAT),
    toDate: formatInTimeZone(endOfDay(now), timezone, DATE_PARSED_FORMAT),
  };
};

export const IntegratorEarnings: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const isIntegratorEarningsWithdrawalEnabled = useIntegratorFeatureFlag(
    FF_DASHBOARD_INTEGRATOR_EARNINGS_WITHDRAWAL,
    false,
  );
  const timezone = React.useContext(TimezoneContext);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { transactionId = '' } = useParams<{ readonly transactionId: string }>();
  const { data, isError, isLoading } = useGetIntegratorTotalEarningsQuery();

  const now = React.useMemo((): Date => new Date(), []);

  const defaultFilters = React.useMemo(
    (): GetTransactionsFilters => ({
      ...GetTransactionsFilters.initial(),
      type: TransactionType.TRANSFER,
      fromDate: format(startOfMonth(now), FILTER_DATE_FORMAT),
      toDate: format(now, FILTER_DATE_FORMAT),
    }),
    [now],
  );

  const datePresets = React.useMemo(
    (): readonly DatePreset[] => [
      {
        label: tr('TODAY'),
        getValue: (): { fromDate: Date; toDate: Date } => ({
          fromDate: startOfDay(now),
          toDate: endOfDay(now),
        }),
      },
      {
        label: tr('THIS_WEEK'),
        getValue: (): { fromDate: Date; toDate: Date } => ({
          fromDate: startOfWeek(now),
          toDate: endOfDay(now),
        }),
      },
      {
        label: tr('THIS_MONTH'),
        getValue: (): { fromDate: Date; toDate: Date } => ({
          fromDate: startOfMonth(now),
          toDate: endOfDay(now),
        }),
      },
    ],
    [tr, now],
  );

  const [filters, setFilters] = useTypedSearchParams<GetTransactionsFilters>(defaultFilters);
  const [filtersModalOpen, setFiltersModalOpen] = React.useState<boolean>(false);

  React.useEffect((): void => {
    if (!search.has('fromDate') || !search.has('toDate')) {
      setFilters({
        fromDate: defaultFilters.fromDate,
        toDate: defaultFilters.toDate,
      } as GetTransactionsFilters);
    }
  }, [defaultFilters.fromDate, defaultFilters.toDate, search, setFilters]);

  const dateRange = React.useMemo((): { fromDate: string; toDate: string } => {
    const defaults = computeDefaultDates(timezone);

    const fromDate = filters.fromDate
      ? formatInTimeZone(
          startOfDay(parse(filters.fromDate, FILTER_DATE_FORMAT, new Date())),
          timezone,
          DATE_PARSED_FORMAT,
        )
      : defaults.fromDate;

    const toDate = filters.toDate
      ? formatInTimeZone(
          endOfDay(parse(filters.toDate, FILTER_DATE_FORMAT, new Date())),
          timezone,
          DATE_PARSED_FORMAT,
        )
      : defaults.toDate;

    return { fromDate, toDate };
  }, [filters.fromDate, filters.toDate, timezone]);

  const {
    data: earningsData,
    isError: isEarningsError,
    isLoading: isEarningsLoading,
  } = useGetIntegratorRevenueSummaryQuery(dateRange);

  const transactionFilters = React.useMemo(
    (): GetTransactionsFilters => ({
      ...filters,
      type: TransactionType.TRANSFER,
      status: SimplifiedTransactionStatus.COMPLETED,
      onlyWithIntegratorSpread: true,
    }),
    [filters],
  );

  const earningsDefaults = React.useMemo(
    (): GetTransactionsFilters => ({
      ...GetTransactionsFilters.initial(),
      type: TransactionType.TRANSFER,
    }),
    [],
  );

  const filtersCount = React.useMemo(
    (): number => countTransactionFilters(filters, earningsDefaults),
    [filters, earningsDefaults],
  );

  const onRowClick = React.useCallback(
    (transactionId: string): void => {
      navigate({
        pathname: transactionId,
        search: search.toString(),
      });
    },
    [navigate, search],
  );

  const closeTransactionDetails = React.useCallback((): void => {
    navigate({
      pathname: '.',
      search: search.toString(),
    });
  }, [navigate, search]);

  const openFiltersModal = React.useCallback((): void => {
    setFiltersModalOpen(true);
  }, []);

  const closeFiltersModal = React.useCallback((): void => {
    setFiltersModalOpen(false);
  }, []);

  const handleFiltersChange = React.useCallback(
    (newFilters: GetTransactionsFilters): void => {
      const { fromDate, toDate, amount, currency, recipientId, beneficiaryId } = newFilters;
      setFilters({
        fromDate,
        toDate,
        amount,
        currency: currency === CurrencyCodeType.ANY ? undefined : currency,
        recipientId,
        beneficiaryId,
      } as GetTransactionsFilters);
      setFiltersModalOpen(false);
    },
    [setFilters],
  );

  const formattedDateRange = React.useMemo((): string => {
    const from = filters.fromDate
      ? format(parse(filters.fromDate, FILTER_DATE_FORMAT, new Date()), 'MMM d')
      : '';
    const to = filters.toDate
      ? format(parse(filters.toDate, FILTER_DATE_FORMAT, new Date()), 'MMM d')
      : '';
    return `${from} – ${to}`;
  }, [filters.fromDate, filters.toDate]);

  return (
    <MainScreen title={tr('EARNINGS_DASHBOARD')}>
      <Typography color="text.secondary" sx={{ mt: -2 }}>
        {tr('INTEGRATOR_EARNINGS_SUBTITLE')}
      </Typography>

      <Paper elevation={3} sx={styles.paper}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            {tr('SUMMARY')}
          </Typography>
          {isIntegratorEarningsWithdrawalEnabled && (
            <Button
              variant="contained"
              size="compact"
              disabled={!data?.unpaidBalance}
              onClick={(): void | Promise<void> => navigate('/earnings/withdraw')}>
              {tr('WITHDRAW')}
            </Button>
          )}
        </Box>
        <Stack sx={styles.cardsRow}>
          {isError ? (
            <Alert severity="error" sx={styles.alert}>
              {tr('ERROR_LOADING_EARNINGS')}
            </Alert>
          ) : (
            <>
              <AmountCard
                label="TOTAL_EARNINGS"
                amount={data?.totalEarnings ?? 0}
                currencyCode={CurrencyCodeType.USD}
                isLoading={isLoading}
              />
              <AmountCard
                label="AVAILABLE_BALANCE"
                amount={data?.unpaidBalance ?? 0}
                currencyCode={CurrencyCodeType.USD}
                isLoading={isLoading}
                tooltip="AVAILABLE_BALANCE_TOOLTIP"
              />
            </>
          )}
        </Stack>
      </Paper>

      <Paper elevation={3} sx={styles.paper}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            {tr('EARNINGS_DETAILS')}
          </Typography>
          <Button variant="light" size="compact" onClick={openFiltersModal}>
            {filtersCount === 0 ? tr('FILTER') : tr('FILTER_WITH_COUNT', filtersCount)}
          </Button>
        </Box>

        {filtersModalOpen && (
          <ApplicationDialog onClose={closeFiltersModal}>
            <ApplicationModal
              title={tr('FILTER')}
              description={tr('SELECT_FILTERS_THAT_APPLY')}
              onClose={closeFiltersModal}>
              <TransactionsFilters
                filters={filters}
                visibleFilters={EARNINGS_VISIBLE_FILTERS}
                datePresets={datePresets}
                onChange={handleFiltersChange}
              />
            </ApplicationModal>
          </ApplicationDialog>
        )}
        <Stack sx={styles.cardsRow}>
          {isEarningsError ? (
            <Alert severity="error" sx={styles.alert}>
              {tr('ERROR_LOADING_EARNINGS')}
            </Alert>
          ) : (
            <>
              <AmountCard
                label="VOLUME_PROCESSED"
                amount={earningsData?.tpv ?? 0}
                currencyCode={CurrencyCodeType.USD}
                isLoading={isEarningsLoading}
                tooltip="VOLUME_PROCESSED_TOOLTIP"
                subtitle={formattedDateRange}
              />
              <AmountCard
                label="MARKUP_EARNED"
                amount={earningsData?.spreadEarnings ?? 0}
                currencyCode={CurrencyCodeType.USD}
                isLoading={isEarningsLoading}
                subtitle={formattedDateRange}
              />
            </>
          )}
        </Stack>
      </Paper>

      <Box display="flex" flexDirection="column" gap={4} mt={3} flex={1}>
        <TransactionsTable
          beneficiaryId={null}
          filters={transactionFilters}
          onFiltersChange={handleFiltersChange}
          onRowClick={onRowClick}
          noContentView={<NoActivityView />}
          noResultsView={
            <NoResultsView
              title={tr('ZERO_RESULTS_FOUND')}
              message={tr('TRY_CHANGING_YOUR_APPLIED_FILTERS', tr('ACTIVITY').toLocaleLowerCase())}
            />
          }
          visibleColumns={EARNINGS_ACTIVITY_COLUMNS}
        />
      </Box>

      <Drawer open={!!transactionId} anchor="right" onClose={closeTransactionDetails}>
        <Outlet />
      </Drawer>
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  paper: {
    p: 5,
    borderRadius: 2,
    mt: 3,
  },
  cardsRow: {
    flexDirection: 'row',
    gap: 4,
  },
  alert: {
    width: '100%',
  },
};
